import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@shared/ui/button";
import { FilterX } from "lucide-react";
import { useChoices, useInventory, useInventoryExport } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";

interface RetiredFilters {
  type: string;
}

const MeterRetiredList = () => {
  const utilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const type = searchParams.get("type") || "";

  const [pendingFilters, setPendingFilters] = useState<RetiredFilters>({
    type: decodeURIComponent(type),
  });

  // Sync pendingFilters when URL params change
  useEffect(() => {
    setPendingFilters({
      type: decodeURIComponent(type),
    });
  }, [type]);

  const handlePendingFilterChange = (key: keyof RetiredFilters, value: string) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const queryParams = useMemo(() => ({
    remoteUtilityId: utilityId,
    tab: "disposed",
    page: currentPage,
    limit: PAGE_SIZE,
    search: searchQuery,
    meter_type: type,
  }), [utilityId, currentPage, searchQuery, type]);

  const { data: disposedList, isLoading } = useInventory(queryParams);
  const { data: meterType } = useChoices({ choice: "meter_type" });
  const { exportCSV, isExporting } = useInventoryExport( utilityId,
    "disposed",
    searchQuery,
    type,
    "");

  const processedData = useMemo(() => {
    return (
      disposedList?.results.map((item) => ({
        ...item,
        make: item?.meterModel?.make,
        model: item?.meterModel?.model,
        id: item.deviceNo,
      })) ?? []
    );
  }, [disposedList]);

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.type && { type: pendingFilters.type }),
    };

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(type && { type }),
      ...(searchTerm && { search: searchTerm }),
    };

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "deviceNo",
      header: "Device Number",
      sortable: true,
    },
    {
      key: "meterTypeDisplay",
      header: "Model & Type",
      type: "badge",
      badgeConfig: {
        variant: "secondary",
      },
    },
    {
      key: "make",
      header: "Manufacturer",
    },
    {
      key: "disposalDate",
      header: "Disposal Date",
    },
    {
      key: "reason",
      header: "Reason",
    },
    {
      key: "lifeSpan",
      header: "Lifespan",
    },
    {
      key: "lastConsumer",
      header: "Last Consumer",
    },
  ];

  const pagination = disposedList
    ? {
        currentPage,
        totalPages: Math.ceil(disposedList.count / PAGE_SIZE),
        totalItems: disposedList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, disposedList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Device Type</label>
          <Select
            value={pendingFilters.type}
            onValueChange={(value) => handlePendingFilterChange("type", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Device Type" />
            </SelectTrigger>
            <SelectContent>
              {meterType && meterType.length > 0 ? (
                meterType.map((type) => (
                  <SelectItem key={type.key} value={String(type.key)}>
                    {type.value}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="no-data">
                  No data available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={resetFilters}>
          <FilterX className="w-4 h-4 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters = type;
  const handleExport = () => {
    exportCSV();
  };

  return (
    <div className="space-y-4">
      <GenericTable
        data={processedData}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        actionsType="icons"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by serial number, model, or device number..."
        searchDebounceMs={500}
        filterPanel={filterPanel}
        onExport={handleExport}
        exportLoading={isExporting}
        emptyMessage="No disposed meters found matching your criteria"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {hasActiveFilters ? "1 filter active" : ""}
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default MeterRetiredList;
